import { faSearch, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { cn } from '@shared/utils/utils';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button } from '../button';
import { Input } from '../input';

type SearchProps = Omit<React.ComponentProps<'input'>, 'value'> & {
  value?: string;
  alwaysExpanded?: boolean;
  searchButtonLabel?: string;
  clearButtonLabel?: string;
  onEnter?: (value?: string) => void;
  dataTestId?: string; // base
  inputDataTestId?: string; // opcional para input
  buttonIconClassName?: string; // clases para el ícono del botón de búsqueda
  buttonClassName?: string; // clases para el botón (texto/borde)
  /** Clases del contenedor externo (flex); útil para `w-full` en barras responsivas */
  wrapperClassName?: string;
};

export const Search = React.forwardRef<HTMLInputElement, SearchProps>(
  (
    {
      alwaysExpanded = false,
      disabled = false,
      searchButtonLabel,
      clearButtonLabel,
      className,
      onKeyDown,
      onEnter,
      onKeyUp,
      dataTestId,
      inputDataTestId,
      value,
      onChange,
      buttonIconClassName,
      buttonClassName,
      wrapperClassName,
      ...props
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value || '');
    const [shouldExecuteSearch, setShouldExecuteSearch] = useState(false);
    const internalRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => internalRef.current!);

    const baseTestId = dataTestId ?? 'search';

    // 🎯 Sincronizar estado interno con prop value cuando cambia externamente
    useEffect(() => {
      const externalValue = value || '';
      if (externalValue !== inputValue) {
        setInputValue(externalValue);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // Ejecutar búsqueda cuando se marque la bandera
    useEffect(() => {
      if (shouldExecuteSearch) {
        onEnter?.(inputValue);
        setShouldExecuteSearch(false);
      }
    }, [shouldExecuteSearch, onEnter, inputValue]);

    // 🎯 Manejar clicks fuera del componente
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          if (!inputValue || inputValue.toString().trim() === '') {
            setIsOpen(false);
          }
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }
    }, [isOpen, inputValue]);

    const handleOpen = () => {
      setIsOpen(true);
      // Enfocar el input después de que se renderice
      setTimeout(() => {
        internalRef.current?.focus();
      }, 0);
    };

    const handleSearchButtonClick = () => {
      if (alwaysExpanded || isOpen || inputValue) {
        onEnter?.(inputValue);
        return;
      }

      handleOpen();
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setInputValue(newValue);
      onChange?.(event);
    };

    const handleClear = () => {
      setInputValue('');
      // Crear un evento sintético para onChange
      if (onChange) {
        const syntheticEvent = {
          target: { value: '' },
          currentTarget: { value: '' },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
      // Marcar que se debe ejecutar la búsqueda después del re-render
      setShouldExecuteSearch(true);
      // Cerrar el input después de limpiar
      setIsOpen(false);
    };

    return (
      <div ref={containerRef} className={cn('flex items-center', wrapperClassName)} data-testid={baseTestId}>
        <Button
          icon={faSearch}
          aria-label={searchButtonLabel}
          disabled={disabled}
          variant="outline"
          size="sm"
          onClick={handleSearchButtonClick}
          classNameIcon={cn('!h-4', buttonIconClassName)}
          data-testid={`${baseTestId}-button`}
          className={cn(
            'shadow-none transition-all w-12 h-8 border border-input hover:text-foreground hover:bg-accent',
            (alwaysExpanded || isOpen || inputValue) && '!border-r-0 !rounded-tr-none rounded-br-none',
            buttonClassName,
          )}
        />

        <div className={cn('relative min-w-0', (alwaysExpanded || isOpen || inputValue) && 'flex-1')}>
          <Input
            ref={internalRef}
            disabled={disabled}
            value={inputValue}
            onChange={handleChange}
            onKeyDown={(event) => {
              onKeyDown?.(event);
              if (event.key === 'Enter') {
                onEnter?.(inputValue);
              }
              if (event.key === 'Escape') {
                if (!inputValue || inputValue.toString().trim() === '') {
                  setIsOpen(false);
                }
              }
            }}
            onKeyUp={(event) => {
              onKeyUp?.(event);
              if (event.key === 'Enter') {
                if (!inputValue || inputValue.toString().trim() === '') {
                  setIsOpen(false);
                }
              }
            }}
            data-testid={inputDataTestId ?? `${baseTestId}-input`}
            {...props}
            className={cn(
              'transition-all w-0 opacity-0 scale-x-0 origin-left h-8',
              'border border-card-border bg-background',
              (alwaysExpanded || isOpen || inputValue) && 'w-full opacity-100 scale-x-100',
              !(alwaysExpanded || isOpen || inputValue) && 'hidden',
              'pl-[10px] !ml-0 border-l-0 rounded-tl-none rounded-bl-none shadow-sm',
              'focus-visible:ring-0 outline-none focus-visible:border-card-border',
              inputValue && 'pr-8',
              className,
            )}
          />

          {inputValue && (
            <Button
              icon={faXmark}
              aria-label={clearButtonLabel}
              disabled={disabled}
              variant="ghost"
              size="sm"
              onClick={handleClear}
              data-testid={`${baseTestId}-clear`}
              className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 p-0 hover:bg-accent"
              classNameIcon="!h-3 !w-3"
            />
          )}
        </div>
      </div>
    );
  },
);

Search.displayName = 'Search';
